import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { isAuthSessionError, recoverFromAuthError } from './authErrorRecovery';

// Rattrapage global des requêtes refusées faute de session (voir
// `authErrorRecovery.ts` pour le pourquoi et l'incident de production qui l'a
// motivé). Sans ce branchement, un 401 PostgREST ne déclenche RIEN : l'écran
// reste vide, sans message, et l'utilisateur croit que ses données ont disparu.
//
// Le rattrapage tourne APRÈS les tentatives (`retry: 1`), donc une seule fois
// par requête réellement perdue.

// Requêtes déjà rejouées depuis le dernier rattrapage réussi.
//
// GARDE ANTI-BOUCLE, indispensable : si le rafraîchissement réussit mais que la
// requête échoue encore (l'utilisateur n'a réellement pas le droit, ou la RLS
// refuse pour une autre raison), rejouer sans mémoire produirait
// 401 → rafraîchir → rejouer → 401 … à l'infini. On ne donne qu'UNE seconde
// chance par requête et par session.
const alreadyReplayed = new Set<string>();

const queryCache = new QueryCache({
  onError: (error, query) => {
    if (!isAuthSessionError(error)) return;

    const hash = query.queryHash;
    if (alreadyReplayed.has(hash)) return;

    void recoverFromAuthError(error).then((outcome) => {
      // Le rafraîchissement a repris : on rejoue la requête perdue. On ne
      // rejoue QUE celle-ci ; invalider tout le cache déclencherait une rafale
      // équivalente à celle qu'on essaie d'éteindre.
      if (outcome !== 'recovered') return;
      alreadyReplayed.add(hash);
      void query.fetch();
    });
  },
});

/** À appeler quand une session neuve est installée : les secondes chances repartent à zéro. */
export function clearAuthReplayGuard(): void {
  alreadyReplayed.clear();
}

// Les écritures suivent la même règle de rattrapage de session : on rafraîchit
// et on prévient, sans rejouer ici.
//
// ⛔ `defaultOptions.mutations.retry` vaut `false` depuis le 2026-09-19 :
// AUCUNE écriture n'est rejouée automatiquement. Avant, `retry: 1` rejouait
// toute mutation échouée une fois, et un remboursement a été cumulé en
// production de cette façon. Une écriture qui échoue laisse la personne
// décider de recommencer. Les lectures, sans danger, gardent `retry: 1`.
// Les `retry: false` posés un à un dans les écritures sensibles
// (`useCreateCashRegisterMovement`, `useRefundOnlinePayment`,
// `useRevokeAccountAccess`...) restent : redondants, ils documentent le geste.
// Verrou : `src/lib/__tests__/queryClientDefaults.test.ts`.
const mutationCache = new MutationCache({
  onError: (error) => {
    if (!isAuthSessionError(error)) return;
    void recoverFromAuthError(error);
  },
});

export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh for 5 min
      gcTime: 10 * 60 * 1000, // 10 minutes - cache persists for 10 min (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      // Network mode optimizations
      networkMode: 'online', // Only fetch when online
    },
    mutations: {
      // ⛔ Aucune écriture n'est rejouée automatiquement (voir le commentaire
      // du `mutationCache` plus haut).
      retry: false,
      networkMode: 'online',
    },
  },
});
